import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { FolderUp, CirclePlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { DialogHeader, DialogTitle, DialogDescription, DialogContent } from '@/components/ui/dialog';
import { SetupSteps, SharefileForm } from '@/components/Integration/Setup';
import { renderLogo } from 'src/utils/renderLogo';
import { Button } from '@/components/ui/button';
import { CreateShareFileIntegrationDocument } from 'src/graphql/actions/integration/createShareFileIntegration.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from 'src/lib/utils';

import { useIntegration } from '../model';
import { refetchIntegrations } from '../api';

const items = [
  <Fragment key="1">
    You <span className="font-bold">submit a folder request</span> with the name and email address of the person you
    want us to send the Sharefile folder invitation to.
  </Fragment>,
  'We’ll create your secure upload folder and connect it to your escrow.',
  <Fragment key="3">
    We invite the representative you nominated in Step 1 to <span className="font-bold">join Sharefile</span>. You can
    request access for additional users at any time.
  </Fragment>,
  <Fragment key="4">
    <span className="font-bold">You can add files and folders</span> to your secure upload folder. Everything you upload
    will form part of your escrowed material.
  </Fragment>,
];

export const StepRenderer = ({ step, setStep }: { step: number; setStep: Dispatch<SetStateAction<number>> }) => {
  const [createShareFileIntegration] = useMutation(CreateShareFileIntegrationDocument, {
    onCompleted: refetchIntegrations,
  });
  const { resetIntegration } = useIntegration();

  switch (step) {
    case 0: {
      const handleClick = () => setStep(1);

      return (
        <div className="flex flex-col gap-6">
          <Card className="bg-muted">
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">How it works</CardTitle>
            </CardHeader>
            <CardContent>
              <SetupSteps
                items={items}
                classes={{
                  item: 'bg-white',
                }}
              />
            </CardContent>
          </Card>
          <div className="flex flex-col items-center pt-2">
            <Separator />
            <div className="text-xs text-muted-foreground font-medium bg-white px-2 -mt-2">
              Or try a faster alternative
            </div>
          </div>
          <Alert className="bg-accent shadow-sm p-6 items-center has-[>svg]:grid-cols-[--spacing(6)_1fr] [&>svg]:size-6">
            <FolderUp className="self-start" />
            <AlertDescription>
              <p className="text-card-foreground text-sm">
                Want to deposit assets directly into escrow? Start a deposit and select a{' '}
                <strong>manual upload method</strong> to skip the folder setup.
              </p>
            </AlertDescription>
          </Alert>
          <Button onClick={handleClick} className="w-full" size="sm">
            <CirclePlusIcon />
            Create a folder request
          </Button>
        </div>
      );
    }
    case 1: {
      const handleSubmit = async ({ email }: { email: string }) => {
        try {
          const { data } = await createShareFileIntegration({
            variables: {
              shareFileIntegrationInput: {
                email,
              },
            },
          });
          if (data?.createShareFileIntegration?.errors?.[0])
            throw new ClientError(data.createShareFileIntegration.errors[0]);
          setStep(2);
        } catch (error) {
          getApiError(error, toast.error);
        }
      };
      const handleBack = () => setStep(0);

      return <SharefileForm onSubmit={handleSubmit} onBack={handleBack} />;
    }
    case 2: {
      return (
        <div className="space-y-6">
          <div className="flex flex-col items-center pt-2">
            <Separator />
            <div className="text-xs text-muted-foreground font-medium bg-white px-2 -mt-2">Or skip the wait</div>
          </div>
          <Alert className="bg-accent shadow-sm p-6 items-center">
            <FolderUp />
            <AlertDescription>
              <p className="text-card-foreground text-sm">
                Want to deposit assets directly into escrow? Start a deposit and select a{' '}
                <strong>manual upload method</strong> to skip the folder setup.
              </p>
            </AlertDescription>
          </Alert>
          <Button onClick={resetIntegration} className="w-full">
            View integrations
          </Button>
        </div>
      );
    }
    default:
      return null;
  }
};

const stepContentMap = {
  0: {
    title: 'Sharefile',
    description:
      'Use SFTP to upload your files directly into a Sharefile folder and deposit them into escrow. People with access to the folder can add any number of files manually.',
    modalWidth: 'md:max-w-2xl',
  },
  1: {
    title: 'Sharefile',
    description: 'Add the details of the person responsible for uploading the assets into escrow.',
    modalWidth: 'md:max-w-xl',
  },
  2: {
    title: 'Your Sharefile folder request has been submitted',
    description: "We've received your request and will send the folder invitation shortly.",
    modalWidth: 'md:max-w-lg',
  },
} as const;

const SharefileIntegrationSetup = () => {
  const [step, setStep] = useState(0);

  const Icon = renderLogo('share_file_https');
  const { title, description, modalWidth } = stepContentMap[step as keyof typeof stepContentMap];

  return (
    <DialogContent className={cn('gap-6 max-h-205 overflow-y-auto', modalWidth)}>
      <DialogHeader className={cn('relative text-center gap-0', step === 1 && 'pl-16 text-left')}>
        {Icon && <Icon className={cn('size-10 mx-auto', step === 1 && 'absolute top-0 left-0')} />}
        <DialogTitle className={cn('text-xl font-bold mb-4', step === 1 && 'mb-1.5')}>{title}</DialogTitle>
        <DialogDescription className="font-medium">{description}</DialogDescription>
      </DialogHeader>
      <StepRenderer step={step} setStep={setStep} />
    </DialogContent>
  );
};

export default SharefileIntegrationSetup;
